// useTuya.js - Estado e controlo dos aparelhos Tuya
//
// Três vistas usam a mesma API Tuya: bomba de calor, VMC e luzes RGB.
// Antes viviam todas dentro do App.js e partilhavam um único `tuyaDevices`
// mais um useEffect com `if (currentView === 'heatpump' || ... )`. Ao passar
// cada vista para o seu componente, essa condição deixa de fazer sentido:
// o componente só existe enquanto a vista está aberta, por isso o intervalo
// arranca ao montar e é limpo ao desmontar.
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const API_URL = '/api';

// Todos os endpoints passaram a exigir autenticacao (antes alguns respondiam
// a qualquer pessoa, o que era o verdadeiro problema).
function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


// 15s é o intervalo que a vista da bomba e da VMC sempre usaram. A Tuya não
// tem o rate limit apertado da Bosch, por isso não há razão para o alargar.
const INTERVALO_MS = 15000;

export function useTuya() {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDevices = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/tuya/devices`, { headers: authHeaders() });
      const data = await response.json();
      if (data.devices) setDevices(data.devices);
    } catch (err) {
      setError(t('erros.buscarTuya'));
    }
  }, [t]);

  const control = useCallback(async (deviceId, commands) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/tuya/${deviceId}/control`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(commands),
      });
      const data = await response.json();
      if (data.success) {
        // A Tuya demora a refletir o comando no estado que devolve; reler
        // logo a seguir traria o valor antigo e a interface piscava.
        setTimeout(fetchDevices, 2000);
      } else {
        setError(t('erros.controlarDispositivo'));
      }
    } catch (err) {
      setError(t('erros.enviarComando'));
    } finally {
      setLoading(false);
    }
  }, [fetchDevices, t]);

  const togglePower = useCallback(
    (deviceId, currentState) => control(deviceId, { switch: !currentState }),
    [control]
  );

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, INTERVALO_MS);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  return { devices, loading, error, fetchDevices, control, togglePower };
}

// Conversões de cor para as luzes RGB. A Tuya trabalha em HSV com a saturação
// e o valor em milésimos (0-1000), não em percentagem — daí os fatores de 1000.
export function hexToHsv(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : Math.round((d / max) * 1000);
  const v = Math.round(max * 1000);
  return { h, s, v };
}

export function hsvToHex(h, s, v) {
  const sN = s / 1000;
  const vN = v / 1000;
  const c = vN * sN;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = vN - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  const toHex = (n) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
